import {GanttChartXAxis} from '@hconnect/ganttchart/src/modules/GanttChartXAxis'
import {useUpmPlant} from '@hconnect/upm'
import {H2, H2Light, LabelSmallRegular, Table, H3} from '@hm/fundamental'
import {HeatZonesView} from '@kilnfeature/modules/heatZones/HeatZonesView'
import {StaticKilnSvgWrapper} from '@widgets/kms'
import moment from 'moment'
import React, {useEffect, useMemo, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {KilnShellSvg} from './KilnShellSvg'
import {useKilnShutdownPrintConfig} from './useKilnShutdownPrintConfig'

import {useKilnShutdownReportQuery} from '@refra/modules/common/hooks/queries'
import {useKilns} from '@refra/modules/common/providers'
import {
  getBrickPattern,
  getBrickFormat,
  getBrickShapesWithSymbol
} from '@refra/modules/common/utils'
import {
  DriveSvg,
  StationSvg
} from '@refra/modules/refra-planning/components/KilnOverviewCard/KilnOverviewCardBody/StaticSvg'
import {getKilnMaterialInstallationsWithColor} from '@refra/modules/refra-planning/components/KilnOverviewCard/KilnOverviewCardBody/StaticSvg/KilnShellSvg/utils'
import {useKilnSettings} from '@refra/modules/refra-planning/providers'
import {
  KilnShutdown,
  ReportMaterialInstallation,
  ReportMaterialInstallationTableData
} from '@refra/modules/refra-planning/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

const DEFAULT_PRINT_WIDTH = 1020

export const KilnShutdownPrintContent = React.forwardRef<
  HTMLDivElement,
  {shutdown: KilnShutdown; onReady: () => void}
>(({shutdown, onReady}, ref) => {
  const plantId = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId})
  const {selectedKiln} = useKilns()
  const {kilnConfig} = useKilnSettings()
  const {t} = useTranslation()

  const heatZones = useMemo(() => kilnConfig.payload.layout.heatZones ?? [], [kilnConfig])
  const kilnLength = Number(kilnConfig.payload.configuration.dimensions.length)

  const {data, isFetching} = useKilnShutdownReportQuery({
    shutdownId: shutdown.id,
    kilnUpmId: selectedKiln.upmId,
    plantUpmId: upmPlant.upmId
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const tableConfig = useKilnShutdownPrintConfig()

  const kilnMaterialInstallationsWithColor =
    getKilnMaterialInstallationsWithColor<ReportMaterialInstallation>(
      data?.materialInstallations ?? [],
      kilnLength
    )

  const kilnShellComponent = useMemo(
    () => KilnShellSvg(kilnMaterialInstallationsWithColor),
    [kilnMaterialInstallationsWithColor]
  )

  useEffect(() => {
    if (!data || isFetching) return
    onReady()
  }, [data, isFetching, onReady])

  const tableData: ReportMaterialInstallationTableData[] =
    data?.materialInstallations.map((materialInstallation) => ({
      ...materialInstallation,
      isMortarRequired: materialInstallation.isMortarRequired ? t('common.yes') : t('common.no'),
      comments: materialInstallation.comments.join(', '),
      pattern: getBrickPattern(materialInstallation.brickCalculation),
      format: getBrickFormat(materialInstallation.brickCalculation),
      symbolKey: getBrickShapesWithSymbol(materialInstallation.brickCalculation)
        ?.map((shape) => `${shape.symbol}-${shape.name}`)
        .join(', ')
    })) ?? []

  return (
    <div ref={ref} className="p-4 flex flex-col gap-4">
      <div className="flex flex-row gap-2">
        <H2>{upmPlant.name}</H2>
        <H2Light>||</H2Light>
        <H2Light>{selectedKiln.displayValue}</H2Light>
      </div>
      <div>
        <H3>{t('refraPlanning.kilnShutdown.report.name')}</H3>
        <div className="flex flex-row gap-2">
          <LabelSmallRegular>{shutdown.name}</LabelSmallRegular>
          <LabelSmallRegular>{moment(shutdown.feedingDate).format('DD.MM.YYYY')}</LabelSmallRegular>
        </div>
      </div>
      <div className="w-full" ref={containerRef}>
        <HeatZonesView
          heatZones={heatZones}
          kilnLength={kilnLength}
          hideXAxis={true}
          zoneHeight={24}
          hideDetails={true}
          width={DEFAULT_PRINT_WIDTH}
          maxWidth={DEFAULT_PRINT_WIDTH}
        />
        <StaticKilnSvgWrapper
          kilnLength={kilnLength}
          kilnLayout={kilnConfig.payload.layout}
          customSvgPreviews={{
            station: StationSvg,
            drive: DriveSvg,
            kilnShell: kilnShellComponent
          }}
          containerWidth={DEFAULT_PRINT_WIDTH}
        />
        <GanttChartXAxis
          canvasWidth={DEFAULT_PRINT_WIDTH}
          kilnLength={kilnLength}
          stepWidth={DEFAULT_PRINT_WIDTH / kilnLength}
          yAxisLabelWidth={0}
        />
      </div>
      <div>
        <Table
          config={tableConfig}
          data={tableData}
          breakpoint="sm"
          data-cy="refra_kiln_shutdown_report_table"
        />
      </div>
    </div>
  )
})

KilnShutdownPrintContent.displayName = 'KilnShutdownPrintContent'
